"use client";

import { useEffect, useState, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useRouter } from "next/navigation";
import { Eye, EyeOff } from "lucide-react";

export default function CompanyRegisterPage() {
    const router = useRouter();
    const [form, setForm] = useState({
        email: "",
        password: "",
        name: "",
        honeypot: "",
    });
    const [agreed, setAgreed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [lang, setLang] = useState<'de' | 'en'>('de');
    const [mounted, setMounted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const emailRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setMounted(true);
        emailRef.current?.focus();
    }, []);

    const texts = {
        de: {
            title: "Firmenregistrierung",
            subtitle: "Nur 30 Sekunden. Erstes Projekt kostenlos!",
            errorFields: "Bitte füllen Sie alle Pflichtfelder aus.",
            errorAgree: "Sie müssen den AGB & Datenschutz zustimmen.",
            loadingText: "Registriere Ihre Firma...",
            placeholders: {
                email: "Email*",
                password: "Passwort*",
                name: "Firmenname / Agentur / Recruiter",
            },
            agreeText: "Ich akzeptiere die",
            registerButton: "Registrieren",
        },
        en: {
            title: "Company Registration",
            subtitle: "Just 30 seconds. First project free!",
            errorFields: "Please fill all required fields.",
            errorAgree: "You must agree to Terms & Privacy.",
            loadingText: "Registering your company...",
            placeholders: {
                email: "Email*",
                password: "Password*",
                name: "Company Name / Agentur / Recruiter",
            },
            agreeText: "I agree to",
            registerButton: "Register",
        },
    };

    const t = texts[lang];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrorMessage("");
    };

    const handleRegister = async () => {
        setErrorMessage("");

        if (form.honeypot) {
            console.warn("Honeypot detected — potential bot");
            return;
        }

        if (!form.email || !form.password || !form.name) {
            setErrorMessage(t.errorFields);
            return;
        }

        if (!agreed) {
            setErrorMessage(t.errorAgree);
            return;
        }

        setLoading(true);

        try {
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: form.email.trim(),
                password: form.password,
            });

            if (authError || !authData.user) {
                setErrorMessage(authError?.message || "Registrierung fehlgeschlagen");
                setLoading(false);
                return;
            }

            const user = authData.user;

            const { error: profileError } = await supabase
                .from("profiles")
                .upsert({
                    id: user.id,
                    email: form.email.trim(),
                    role: "company",
                    locale: lang,
                    has_onboarded: false,
                });

            if (profileError) {
                setErrorMessage("Profil konnte nicht erstellt werden: " + profileError.message);
                setLoading(false);
                return;
            }

            const { error: companyError } = await supabase
                .from("companies")
                .insert({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    owner_id: user.id,
                    locale: lang,
                    verified: false,
                    avatar_url: null,
                });

            if (companyError) {
                setErrorMessage("Firmenprofil konnte nicht erstellt werden: " + companyError.message);
                setLoading(false);
                return;
            }

            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: form.email.trim(),
                password: form.password,
            });

            if (signInError) {
                console.warn("Autologin failed:", signInError);
            }

            // Изменил редирект на страницу создания проекта
            router.push("/company-dashboard/projects/create");
        } catch (err: any) {
            const message = err.message || "Ein unbekannter Fehler ist aufgetreten";
            setErrorMessage(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div
                className={`max-w-md w-full bg-white shadow-xl rounded-2xl p-8 transform transition-all duration-700 ${
                    mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-10"
                }`}
            >
                {/* Language switch */}
                <div className="flex justify-end mb-4">
                    <div className="flex bg-gray-200 rounded-full p-1 w-max relative cursor-pointer">
                        <div
                            className={`absolute top-0 left-0 h-full w-1/2 bg-gray-800 rounded-full transition-transform duration-300 ${
                                lang === "en" ? "translate-x-full" : "translate-x-0"
                            }`}
                        />
                        {(["de", "en"] as const).map((l) => (
                            <div
                                key={l}
                                onClick={() => setLang(l)}
                                className={`z-10 px-4 py-2 text-center rounded-full transition-colors duration-300 font-medium cursor-pointer select-none ${
                                    lang === l ? "text-white" : "text-gray-700"
                                }`}
                            >
                                {l.toUpperCase()}
                            </div>
                        ))}
                    </div>
                </div>

                <h1 className="text-2xl font-bold mb-2 text-center text-gray-900">{t.title}</h1>
                <p className="text-gray-600 text-center mb-6 text-sm">{t.subtitle}</p>

                {errorMessage && (
                    <p className="text-red-600 mb-4 text-center font-medium animate-fade">{errorMessage}</p>
                )}

                <div className="space-y-4">
                    {["email", "password", "name"].map((field) => (
                        <div key={field} className="relative">
                            <input
                                ref={field === "email" ? emailRef : null}
                                name={field}
                                type={field === "password" ? (showPassword ? "text" : "password") : "text"}
                                placeholder={t.placeholders[field as keyof typeof t.placeholders]}
                                value={form[field as keyof typeof form]}
                                onChange={handleChange}
                                className="w-full border border-gray-300 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-700 focus:border-transparent transition shadow-sm hover:shadow-md pr-10"
                                disabled={loading}
                            />

                            {field === "password" && (
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none transition"
                                    disabled={loading}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5" />
                                    ) : (
                                        <Eye className="h-5 w-5" />
                                    )}
                                </button>
                            )}
                        </div>
                    ))}

                    {/* Honeypot */}
                    <input
                        name="honeypot"
                        type="text"
                        style={{ display: "none", position: "absolute", left: "-9999px" }}
                        placeholder="Leave empty (for bots)"
                        value={form.honeypot}
                        onChange={handleChange}
                    />

                    <label className="flex items-center gap-2 text-sm mt-2">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 focus:ring-2 focus:ring-gray-700"
                            disabled={loading}
                        />
                        {t.agreeText}{" "}
                        <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-gray-800 underline hover:text-gray-900">
                            AGB
                        </a>{" "}
                        und{" "}
                        <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-gray-800 underline hover:text-gray-900">
                            Datenschutz
                        </a>
                    </label>

                    <button
                        onClick={handleRegister}
                        disabled={loading}
                        className="w-full bg-gray-900 text-white py-3 rounded-xl hover:bg-black transition relative flex justify-center items-center font-medium disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                    >
                        {loading && (
                            <div className="absolute left-4 w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        )}
                        <span>{loading ? t.loadingText : t.registerButton}</span>
                    </button>
                </div>

                <p className="text-center text-sm text-gray-600 mt-6">
                    Schon ein Konto?{" "}
                    <a href="/login" className="underline hover:text-gray-900 font-medium">
                        Einloggen
                    </a>
                </p>
            </div>
        </div>
    );
}